import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  deleteAgenticConversation,
  getAgenticConversation,
  listAgenticConversations,
  streamAgenticChat,
  type AgenticChatTurn,
  type AgenticRuntime,
  type AgenticStreamHandlers,
} from './api'

export const agenticConversationsKey = (clientId: string | null) =>
  ['clients', clientId, 'agentic-ai', 'conversations'] as const

export function useAgenticConversations(clientId: string | null) {
  return useQuery({
    queryKey: agenticConversationsKey(clientId),
    queryFn: () => listAgenticConversations(clientId!),
    enabled: !!clientId,
  })
}

export function useAgenticConversation(clientId: string | null, conversationId: string | null) {
  return useQuery({
    queryKey: [...agenticConversationsKey(clientId), conversationId] as const,
    queryFn: () => getAgenticConversation(clientId!, conversationId!),
    enabled: !!clientId && !!conversationId,
  })
}

export function useAgenticChat(clientId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      message,
      history,
      conversationId,
      runtime,
      ...handlers
    }: {
      message: string
      history: AgenticChatTurn[]
      conversationId?: string | null
      runtime: AgenticRuntime
    } & AgenticStreamHandlers) =>
      streamAgenticChat(clientId!, message, history, conversationId, runtime, handlers),
    onSuccess: async (data) => {
      if (clientId) {
        await Promise.all([
          qc.invalidateQueries({ queryKey: agenticConversationsKey(clientId) }),
          qc.invalidateQueries({
            queryKey: [...agenticConversationsKey(clientId), data.conversation_id],
          }),
        ])
      }
    },
    onSettled: () => {
      if (clientId) {
        qc.invalidateQueries({ queryKey: ['clients', clientId, 'ai-usage'] })
      }
    },
  })
}

export function useDeleteAgenticConversation(clientId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (conversationId: string) => deleteAgenticConversation(clientId!, conversationId),
    onSuccess: () => {
      if (clientId) qc.invalidateQueries({ queryKey: agenticConversationsKey(clientId) })
    },
  })
}
